import type { ChaptersInCourse as ChaptersInCourseAPI } from '@jojotique/server'
import type { ChaptersInCourseElement, ChapterStatus } from '@core/components/course/Chapters'

export const courseChaptersFactory = (BaseElement: typeof HTMLElement): ChaptersInCourseElement => {
  class ChaptersInCourse extends BaseElement implements ChaptersInCourseElement {
    private chaptersContainer: HTMLUListElement

    private placeholderDescriptions: string[] = [
      'Réseau corrompu, données manquantes.',
      "Protocole d'accès en cours de déchiffrage.",
      'Erreur 404 : description introuvable dans le cyber-espace.',
      'Les données de ce chapitre ont été cryptées. Seul un esprit affûté peut les déverrouiller.',
    ]

    constructor() {
      super()

      this.attachShadow({ mode: 'open' })
      this.shadowRoot!.innerHTML = `
      <style>
        @keyframes pulse {
          50% {
            opacity: 0.5;
          }
        }

        ul {
          margin: 0 0 1rem;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 1rem;

          li {
            --chapter-color: var(--jq-primary-color);
            --chapter-bg: var(--jq-primary-bg);
            --chapter-color-title: var(--jq-primary-dark);

            @media (prefers-color-scheme: dark) {
              --chapter-color-title: var(--jq-primary-light);
            }

            container-type: normal;
            container-name: chapter-list;
            border-radius: 0.5rem;
            border: 1px solid var(--chapter-color);
            box-shadow: 0 0 5px var(--chapter-color);

            &.completed {
              --chapter-color: var(--jq-success-color);
              --chapter-bg: var(--jq-success-bg);
              --chapter-color-title: var(--jq-success-dark);

              @media (prefers-color-scheme: dark) {
                --chapter-color-title: var(--jq-success-light);
              }
            }

            &.locked, &.coming-soon {
              --chapter-color: var(--jq-deactivated-color);
              --chapter-bg: var(--jq-deactivated-bg);
              --chapter-color-title: var(--jq-deactivated-dark);

              @media (prefers-color-scheme: dark) {
                --chapter-color-title: var(--jq-deactivated-light);
              }

              &, a {
                box-shadow: none;
              }
            }

            &.coming-soon {
              h3 {
                display: flex;
                align-items: center;
                gap: 0.5rem;

                &:after {
                  content: '(Bientôt disponible)';
                  font-size: 0.875rem;
                  color: var(--jq-text-color-mute);
                }
              }
            }

            &.not-started {
              a {
                border-color: transparent;
              }
            }

            &.in-progress {
              a {
                svg {
                  @media (prefers-reduced-motion: no-preference) {
                    animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                  }
                }
              }
            }

            &:hover:not(.locked), &:not(.locked):has(a:focus) {
              background-color: var(--chapter-bg);

              @media (prefers-reduced-motion: no-preference) {
                transform: scale(1.005);
              }
            }

            a {
              border-radius: 0.5rem;
              padding: 1rem;
              display: flex;
              justify-content: space-between;
              text-decoration: none;
              color: var(--chapter-color);
              border-left: 0.25rem solid var(--chapter-color);
              box-shadow: 0 0 10px var(--chapter-color);

              @media (prefers-color-scheme: dark) {
                box-shadow: 0 0 10px var(--chapter-color) inset;
              }

              > div {
                display: flex;

                &:first-of-type {
                  gap: 1rem;

                  > div {
                    &:first-of-type {
                      width: 5rem;
                      padding: 0.25rem 0;
                      font-family: var(--jq-text-font-techno);
                    }
                  }

                  h3, p {
                    margin: 0;
                  }

                  h3 {
                    font-family: var(--jq-text-font-techno);
                    font-size: 1.25rem;
                    line-height: 1.5;
                    color: var(--chapter-color-title);
                    font-weight: normal;
                  }

                  p {
                    color: var(--jq-text-color-mute);
                  }
                }

                &:last-of-type {
                  justify-content: center;
                  align-items: center;
                  color: var(--chapter-color-title);

                  svg {
                    width: 1.5rem;
                    height: 1.5rem;
                    fill: var(--chapter-color);
                  }
                }
              }
            }

            /*
            Ne fonctionne pas avec Firefox pour le moment.

            @container chapter-list style(--completed: true) {
              a {
                border-color: var(--success-color);
              }
            }

            @container chapter-list style(--in-progress: true) {
              a {
                border-color: var(--chapter-color;
              }
            }

            @container chapter-list style(--locked: true) {
              a {
                border-color: var(--secondary-color);
              }
            }
            */
          }
        }
      </style>
      <ul></ul>
    `

      this.chaptersContainer = this.shadowRoot!.querySelector<HTMLUListElement>('ul')!
    }

    initList(courseSlug: string, chapters: ChaptersInCourseAPI[], document: Document): void {
      this.chaptersContainer.innerHTML = ''
      chapters.forEach((chapter, i) => {
        // TODO: faire le système de complétion des chapitres quand il sera disponible
        let status: ChapterStatus = 'not-started'

        if (chapter.publishedAt === null || new Date(chapter.publishedAt) > new Date()) {
          status = 'coming-soon'
        }

        const chapterItem = document.createElement('li')
        chapterItem.classList.add(status)

        const chapterLink = document.createElement('a')
        chapterLink.href = `/course/${courseSlug}/chapter/${chapter.slug}`

        // @ts-ignore: status ne pour l'instant pas être "locked" => TODO: à supprimer quand le système de complétion des chapitres sera disponible
        if (status === 'locked' || status === 'coming-soon') {
          chapterLink.addEventListener('click', (e) => e.preventDefault())
          chapterLink.title =
            // @ts-ignore: status ne pour l'instant pas être "locked" => TODO: à supprimer quand le système de complétion des chapitres sera disponible
            status === 'locked'
              ? 'Section de données corrompue. Complétez les modules précédents.'
              : "Transmission quantique en cours. Accès d'ici peu."
          chapterLink.href = ''
        }

        const chapterText = document.createElement('div')

        const chapterImgNumber = document.createElement('div')
        chapterImgNumber.innerText = `Chap. ${i + 1}`
        // const chapterImg = document.createElement('img')
        // chapterImg.src = getImgUrl(chapter)
        chapterText.appendChild(chapterImgNumber)

        const chapterTitleDescription = document.createElement('div')

        const chapterTitle = document.createElement('h3')
        chapterTitle.innerText = chapter.title

        chapterTitleDescription.appendChild(chapterTitle)

        const chapterDescription = document.createElement('p')
        chapterDescription.innerText =
          chapter.abstract ||
          this.placeholderDescriptions[Math.floor(Math.random() * this.placeholderDescriptions.length)]
        chapterTitleDescription.appendChild(chapterDescription)

        chapterText.appendChild(chapterTitleDescription)

        chapterLink.appendChild(chapterText)

        const chapterTimeIcon = document.createElement('div')

        const chapterTime = document.createElement('div')
        // TODO: ajouter le temps quand il sera disponible
        // chapterTime.innerText = '~ 30 min'
        chapterTimeIcon.appendChild(chapterTime)
        chapterTimeIcon.appendChild(this.icon(status))

        chapterLink.appendChild(chapterTimeIcon)

        chapterItem.appendChild(chapterLink)

        this.chaptersContainer.appendChild(chapterItem)
      })
    }

    private iconPath(type: ChapterStatus): string {
      return (
        {
          'in-progress': 'M13 10V3L4 14h7v7l9-11h-7z',
          'locked':
            'M12,17A2,2 0 0,0 14,15C14,13.89 13.1,13 12,13A2,2 0 0,0 10,15A2,2 0 0,0 12,17M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V10C4,8.89 4.9,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z',
          'completed':
            'M12 2C6.5 2 2 6.5 2 12S6.5 22 12 22 22 17.5 22 12 17.5 2 12 2M10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z',
          'not-started':
            'M12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22C17.5 22 22 17.5 22 12S17.5 2 12 2M17 13H11V7H12.5V11.5H17V13Z',
          'coming-soon':
            'M11 8H13V14H11V8M13 19.92C12.67 19.97 12.34 20 12 20C8.13 20 5 16.87 5 13S8.13 6 12 6C14.82 6 17.24 7.67 18.35 10.06C18.56 10.04 18.78 10 19 10C19.55 10 20.07 10.11 20.57 10.28C20.23 9.22 19.71 8.24 19.03 7.39L20.45 5.97C20 5.46 19.55 5 19.04 4.56L17.62 6C16.07 4.74 14.12 4 12 4C7.03 4 3 8.03 3 13S7.03 22 12 22C12.42 22 12.83 21.96 13.24 21.91C13.09 21.53 13 21.12 13 20.7V19.92M15 1H9V3H15V1M23 17.3V20.8C23 21.4 22.4 22 21.7 22H16.2C15.6 22 15 21.4 15 20.7V17.2C15 16.6 15.6 16 16.2 16V14.5C16.2 13.1 17.6 12 19 12S21.8 13.1 21.8 14.5V16C22.4 16 23 16.6 23 17.3M20.5 14.5C20.5 13.7 19.8 13.2 19 13.2S17.5 13.7 17.5 14.5V16H20.5V14.5Z',
        } as Record<ChapterStatus, string>
      )[type]
    }

    private icon(type: ChapterStatus): SVGSVGElement {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svg.setAttribute('viewBox', '0 0 24 24')
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      path.setAttribute('d', this.iconPath(type))

      if (type === 'in-progress') {
        path.setAttribute('fill', 'none')
        path.setAttribute('stroke', 'currentColor')
        path.setAttribute('stroke-width', '2')
      }

      svg.appendChild(path)
      return svg
    }
  }

  return ChaptersInCourse
}
